import {
  type InvitationInfo,
  type LoginResponse,
  type MfaEnrollConfirmResponse,
  type MfaEnrollStartResponse,
  type MfaVerifyRequest,
  TRUSTED_DEVICE_DAYS,
} from '@platform/contracts';
import { BrandHero, Button } from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Copy, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { type SubmitEvent, type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { BRAND_NAME, BrandLogo } from '../components/brand';
import { Field, FormError, fd } from '../components/form';
import { useDocumentTitle } from '../components/page';
import { api, errorMessage } from '../lib/api';

export function AuthLayout({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  useDocumentTitle(title);
  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/*
        Panel de marca: azul noche con el logo original (blanco) y, como héroe, la "M" del isotipo
        convertida en un bus de circuito que alimenta lo que hace la consola: envíos,
        conversaciones y tiendas encontradas.
      */}
      <aside className="relative hidden overflow-hidden bg-brand-navy px-12 pt-12 pb-10 text-white lg:flex lg:flex-col">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-circuit opacity-[0.07] mask-b-from-20% mask-b-to-70%"
        />
        <BrandLogo tone="light" className="relative h-14 self-start" />
        <div className="relative flex min-h-0 flex-1 items-center justify-center py-6">
          <BrandHero className="max-h-[26rem] max-w-[34rem]" />
        </div>
        <div className="relative max-w-lg">
          <p className="font-display text-3xl leading-tight font-semibold">
            Tus campañas, tus respuestas y tus leads, en un solo lugar.
          </p>
          <p className="mt-3 max-w-md text-base text-white/75">
            Mira cómo van los envíos, contesta a quien te escribió y descubre qué tiendas están
            interesadas.
          </p>
        </div>
        <p className="relative mt-10 text-sm text-white/60">
          © {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </aside>
      <main className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <BrandLogo tone="ink" className="h-11 dark:hidden" />
            <BrandLogo tone="light" className="hidden h-11 dark:block" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description ? <p className="mt-2 text-sm text-muted-foreground">{description}</p> : null}
          <div className="mt-6">{children}</div>
        </div>
      </main>
    </div>
  );
}

/** Después de cualquier paso de login, a dónde ir según el estado del 2FA. */
export function useAfterLogin() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (res: LoginResponse) => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    if (res.mfa === 'pending') await navigate({ to: '/login/2fa' });
    else if (res.mfa === 'enroll_required') await navigate({ to: '/login/2fa/configurar' });
    else await navigate({ to: '/overview' });
  };
}

export function LoginPage() {
  const after = useAfterLogin();
  const login = useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<LoginResponse>('/api/auth/login', { method: 'POST', body }),
    onSuccess: (res) => after(res),
  });
  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    login.mutate({ email: fd(f, 'email'), password: fd(f, 'password') });
  };
  return (
    <AuthLayout title="Iniciar sesión" description="Entra con tu email y tu contraseña.">
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field label="Email" name="email" type="email" autoComplete="username" required />
        <Field
          label="Contraseña"
          name="password"
          type="password"
          autoComplete="current-password"
          required
        />
        <FormError message={login.error ? errorMessage(login.error) : null} />
        <Button type="submit" disabled={login.isPending}>
          {login.isPending ? 'Entrando…' : 'Entrar'}
        </Button>
        <Link
          to="/olvide"
          className="text-center text-sm text-muted-foreground hover:text-foreground"
        >
          Olvidé mi contraseña
        </Link>
      </form>
    </AuthLayout>
  );
}

export function MfaVerifyPage() {
  const after = useAfterLogin();
  const [recovery, setRecovery] = useState(false);
  const verify = useMutation({
    mutationFn: (body: MfaVerifyRequest) =>
      api<LoginResponse>('/api/auth/mfa/verify', { method: 'POST', body }),
    onSuccess: (res) => after(res),
  });
  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const value = fd(f, 'code').trim();
    const rememberDevice = f.get('rememberDevice') === 'on';
    verify.mutate(
      recovery
        ? { recoveryCode: value.toLowerCase(), rememberDevice }
        : { code: value, rememberDevice },
    );
  };
  return (
    <AuthLayout
      title="Verificación en dos pasos"
      description={
        recovery
          ? 'Escribe uno de tus códigos de recuperación.'
          : 'Escribe el código de 6 dígitos de tu app de autenticación.'
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field
          key={recovery ? 'r' : 'c'}
          label={recovery ? 'Código de recuperación' : 'Código'}
          name="code"
          inputMode={recovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          placeholder={recovery ? 'xxxxx-xxxxx' : '123456'}
          autoFocus
          required
        />
        {/* La ayuda va fuera del <label>: así el nombre accesible del checkbox no contiene
            "código" y getByLabel('Código') sigue apuntando sólo al campo del código. */}
        <div className="flex items-start gap-2 text-sm">
          <input
            id="remember-device"
            type="checkbox"
            name="rememberDevice"
            aria-describedby="remember-device-hint"
            className="mt-0.5 size-4 shrink-0"
          />
          <div>
            <label htmlFor="remember-device">
              Recordar este dispositivo por {TRUSTED_DEVICE_DAYS} días
            </label>
            <p id="remember-device-hint" className="text-xs text-muted-foreground">
              No te pediremos el código en este navegador durante {TRUSTED_DEVICE_DAYS} días. No lo
              marques en un ordenador compartido.
            </p>
          </div>
        </div>
        <FormError message={verify.error ? errorMessage(verify.error) : null} />
        <Button type="submit" disabled={verify.isPending}>
          Verificar
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setRecovery(!recovery);
          }}
        >
          {recovery ? 'Usar el código de la app' : 'Usar un código de recuperación'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function MfaEnrollPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = useQuery({
    queryKey: ['mfa-enroll'],
    queryFn: () => api<MfaEnrollStartResponse>('/api/auth/mfa/enroll/start', { method: 'POST' }),
    staleTime: Infinity,
    gcTime: 0,
  });
  const confirm = useMutation({
    mutationFn: (code: string) =>
      api<MfaEnrollConfirmResponse>('/api/auth/mfa/enroll/confirm', {
        method: 'POST',
        body: { code },
      }),
    onSuccess: (res) => {
      setCodes(res.recoveryCodes);
    },
  });

  if (codes) {
    const text = codes.join('\n');
    return (
      <AuthLayout
        title="Guarda tus códigos de recuperación"
        description="Se muestran una sola vez. Cada uno sirve para entrar una vez si pierdes el teléfono."
      >
        <ul className="mb-4 grid grid-cols-2 gap-2 rounded-md border bg-muted p-3 font-mono text-sm">
          {codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="mb-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(text).then(() => toast.success('Copiados'));
            }}
          >
            <Copy /> Copiar
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
              download="codigos-recuperacion-metamindia.txt"
            >
              <Download /> Descargar
            </a>
          </Button>
        </div>
        <Button
          className="w-full"
          onClick={() => {
            void qc
              .invalidateQueries({ queryKey: ['me'] })
              .then(() => navigate({ to: '/overview' }));
          }}
        >
          Ya los guardé
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Activa la verificación en dos pasos"
      description="Tu cuenta la exige (por tu rol o porque tu empresa la pide a todo el equipo). Escanea el código con Google Authenticator, 1Password o similar."
    >
      {start.data ? (
        <div className="flex flex-col gap-4">
          <div className="flex justify-center rounded-md bg-white p-4">
            <QRCodeSVG
              value={start.data.otpauthUrl}
              size={176}
              aria-label="Código QR para la app de autenticación"
            />
          </div>
          <p className="break-all text-center font-mono text-xs text-muted-foreground">
            {start.data.secret}
          </p>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              confirm.mutate(fd(new FormData(e.currentTarget), 'code').trim());
            }}
          >
            <Field
              label="Código de 6 dígitos"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
            <FormError message={confirm.error ? errorMessage(confirm.error) : null} />
            <Button type="submit" disabled={confirm.isPending}>
              Activar
            </Button>
          </form>
        </div>
      ) : (
        <FormError message={start.error ? errorMessage(start.error) : null} />
      )}
    </AuthLayout>
  );
}

export function ForgotPage() {
  const [sent, setSent] = useState(false);
  const forgot = useMutation({
    mutationFn: (email: string) =>
      api('/api/auth/password/forgot', { method: 'POST', body: { email } }),
    onSuccess: () => {
      setSent(true);
    },
  });
  return (
    <AuthLayout
      title="Restablecer contraseña"
      description="Te enviaremos un enlace si el email tiene una cuenta."
    >
      {sent ? (
        <p className="text-sm">
          Si el email existe, en unos minutos te llegará el enlace (caduca en 1 hora).
        </p>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            forgot.mutate(fd(new FormData(e.currentTarget), 'email'));
          }}
        >
          <Field label="Email" name="email" type="email" autoComplete="username" required />
          <FormError message={forgot.error ? errorMessage(forgot.error) : null} />
          <Button type="submit" disabled={forgot.isPending}>
            Enviar enlace
          </Button>
        </form>
      )}
      <Link
        to="/login"
        className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground"
      >
        Volver
      </Link>
    </AuthLayout>
  );
}

export function ResetPage() {
  const { token } = useSearch({ strict: false });
  const navigate = useNavigate();
  const reset = useMutation({
    mutationFn: (password: string) =>
      api('/api/auth/password/reset', { method: 'POST', body: { token, password } }),
    onSuccess: () => {
      toast.success('Contraseña actualizada. Ya puedes entrar.');
      void navigate({ to: '/login' });
    },
  });
  return (
    <AuthLayout
      title="Elige una contraseña nueva"
      description="Mínimo 12 caracteres. Se cierran tus otras sesiones."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate(fd(new FormData(e.currentTarget), 'password'));
        }}
      >
        <Field
          label="Contraseña nueva"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={12}
          required
        />
        <FormError
          message={
            reset.error
              ? errorMessage(reset.error)
              : !token
                ? 'El enlace está incompleto: vuelve a abrirlo desde el email'
                : null
          }
        />
        <Button type="submit" disabled={reset.isPending || !token}>
          Guardar
        </Button>
      </form>
    </AuthLayout>
  );
}

export function InvitationPage() {
  const { token } = useSearch({ strict: false });
  const after = useAfterLogin();
  const info = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api<InvitationInfo>(`/api/auth/invitations/${encodeURIComponent(token ?? '')}`),
    enabled: Boolean(token),
  });
  const accept = useMutation({
    mutationFn: (body: { name: string; password: string }) =>
      api<LoginResponse>('/api/auth/invitations/accept', {
        method: 'POST',
        body: { ...body, token },
      }),
    onSuccess: (res) => after(res),
  });
  if (!token || info.error) {
    return (
      <AuthLayout title="Invitación">
        <FormError
          message={info.error ? errorMessage(info.error) : 'El enlace de invitación no es válido.'}
        />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      title="Crea tu cuenta"
      description={
        info.data
          ? `Te invitaron a ${info.data.scopeName} como ${info.data.role}.`
          : 'Cargando invitación…'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          accept.mutate({ name: fd(f, 'name'), password: fd(f, 'password') });
        }}
      >
        <Field label="Email" value={info.data?.email ?? ''} readOnly disabled />
        <Field label="Nombre" name="name" autoComplete="name" required />
        <Field
          label="Contraseña"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={12}
          hint="Mínimo 12 caracteres."
          required
        />
        <FormError message={accept.error ? errorMessage(accept.error) : null} />
        <Button type="submit" disabled={accept.isPending || !info.data}>
          Crear cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}
